// exams ux/presentation.tsx AvatarPhoto — deterministic initials-hash fallback; the replace-photo overlay is chrome over a photo (DEV-GUIDE-002 §12.1: bg-chrome = transparent-black-700 + white, both themes — the source's black at 45 % and the first port's neutral at 60 % both retired); app-specific font-heading dropped.
import { Show, createMemo } from "solid-js";
import { Upload } from "lucide-solid";
import { hueForName, huePair } from "../category-hue/index.js";

/* The initials tile wears one of the NINE categorization hues (100-tint
   ground + 900 ink, both themes) — an avatar fallback is exactly what
   Spectrum sanctions these for: identity, beside the initials, never status
   (DEV-GUIDE-002 §12.5). The pair table and the name hash are
   @erp/ui/category-hue's; this file carried its own copy of both until
   2026-09-17, and its hash was CASE-SENSITIVE where lib's was not, so "Asha
   Rao" could wear two hues on two screens (audit C-04). */

/* The size ramp by duty; each step is a Spectrum spacing token, never a
   hand-picked pixel. xl = size-20 = 80px (spacing-900) is the profile HERO
   tile, the one that breaks out of EntityProfileHeader's cover band — the
   patron hero used to carry an 84px/3px hand-rolled circle (audit L5). */
const AVATAR_SIZE: Record<"sm" | "md" | "lg" | "xl", string> = {
  sm: "h-9 w-9 text-xs",
  md: "h-12 w-12 text-sm",
  lg: "h-16 w-16 text-lg",
  xl: "size-20 text-2xl",
};

/**
 * One or two letters for a name — the kit's ONE initials rule, exported so a
 * caller that needs the glyph without this component's tile (a 20px publisher
 * dot beside a title) does not write a second one that disagrees. Pair it with
 * `avatarTone` and the dot matches every other initials surface.
 */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return "?";
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? first;
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

/**
 * The deterministic name→hue mapping, exported so EVERY initials fallback in
 * the kit (sidebar account row, profile panel) and in the modules lands on the
 * same colour for the same person. Returns one complete
 * `bg-<hue>-tint text-<hue>-ink` pair — `huePair(hueForName(name))`, the
 * kit's ONE hash (trimmed, lower-cased).
 */
export function avatarTone(name: string): string {
  return huePair(hueForName(name));
}

export interface AvatarPhotoProps {
  /** The photo's URL. Omit it and the tile shows the name's initials on the name-hashed hue pair. */
  src?: string;
  /** The person's name — the `alt` of the photo, and the source of the initials and of their hue. */
  name: string;
  /** `sm` 36px (table rows), `md` 48px (cards, the default), `lg` 64px (a profile panel), `xl` 80px (the profile hero, breaking out of EntityProfileHeader's cover band). */
  size?: "sm" | "md" | "lg" | "xl";
  /** When provided, a hover/focus overlay lets the user replace the photo. */
  onUpload?: () => void;
  /** Automation id root: the replace-photo button is `${id}-upload`. */
  id?: string;
}

export function AvatarPhoto(props: AvatarPhotoProps) {
  const size = () => props.size ?? "md";
  const tone = createMemo(() => avatarTone(props.name));
  // rounded-700: the old 12px frame was off Spectrum's corner scale (it jumps
  // 10 -> 16), so the tile family lands on corner-radius-700 with StatCard's chip.
  return (
    <div class={`group relative shrink-0 overflow-hidden rounded-700 ${AVATAR_SIZE[size()]}`}>
      <Show
        when={props.src}
        fallback={
          <div class={`grid h-full w-full place-items-center font-bold ${tone()}`}>
            {initialsOf(props.name)}
          </div>
        }
      >
        {(src) => <img src={src()} alt={props.name} class="h-full w-full object-cover" />}
      </Show>
      <Show when={props.onUpload}>
        <button
          type="button"
          id={props.id ? `${props.id}-upload` : undefined}
          onClick={() => props.onUpload?.()}
          aria-label="Replace photo"
          // Painted NOTHING at rest and the chrome pair on hover / keyboard
          // focus — a reveal by colour, not by opacity (§12.6 leaves no
          // opacity utility outside the scrim/shadow/state/skeleton set).
          // The button is in the tree throughout, so it stays focusable.
          class="absolute inset-0 grid cursor-pointer place-items-center bg-transparent text-transparent transition-colors group-hover:bg-chrome group-hover:text-chrome-content focus-visible:bg-chrome focus-visible:text-chrome-content"
        >
          <Upload size={size() === "sm" ? 13 : size() === "xl" ? 20 : 16} />
        </button>
      </Show>
    </div>
  );
}
